import { useState } from 'react';
import type { FormEvent } from 'react';

const SUBMIT_ADDR = 'https://readdy.ai/api/form/dahrm83oh653ivfvo46g';

export default function SubscribeForm() {
  const [status, setStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const formData = new FormData(form);

    const honeypot = String(formData.get('contact_alt') || '').trim();
    if (honeypot) {
      setStatus('success');
      setFormError('');
      form.reset();
      return;
    }
    formData.delete('contact_alt');

    const body = new URLSearchParams();
    formData.forEach((value, key) => {
      if (typeof value === 'string') {
        body.append(key, value);
      }
    });

    setStatus('submitting');
    setFormError('');

    try {
      const response = await fetch(SUBMIT_ADDR, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
      });

      const responseText = await response.text();
      let parsed: { code?: string; message?: string; meta?: { message?: string; detail?: string } } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      const serverMsg =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || '';
      const isOkCode = parsed?.code === 'OK';
      const isSpam = String(serverMsg).toLowerCase().includes('spam');

      if (!response.ok || !isOkCode || isSpam) {
        setStatus('error');
        setFormError(serverMsg || '提交失败，请稍后重试。');
        return;
      }

      setStatus('success');
      setFormError('');
      form.reset();
    } catch {
      setStatus('error');
      setFormError('网络异常，暂时无法提交，请稍后再试。');
    }
  };

  return (
    <section id="subscribe" className="w-full bg-primary-800">
      <div className="mx-auto grid max-w-[1400px] grid-cols-1 items-center gap-10 px-4 py-14 md:px-8 md:py-20 lg:grid-cols-2 lg:gap-16">
        <div>
          <span className="inline-flex items-center gap-2 rounded-full bg-primary-700 px-3 py-1 text-xs font-semibold tracking-[0.2em] text-accent-300">
            <i className="ri-notification-3-line" />
            订阅安全提醒
          </span>
          <h2 className="mt-5 font-heading text-3xl font-black leading-tight text-background-50 md:text-4xl">
            官方验证一有更新，第一时间告诉你
          </h2>
          <p className="mt-4 max-w-lg text-sm leading-relaxed text-primary-100 md:text-base">
            留下邮箱，当我们更新官方验证结果、新增钓鱼手法或调整辨别要点时，会发一封简短提醒。不推销、不打扰，只为让你的每一次访问都更安全。
          </p>
          <ul className="mt-6 flex flex-col gap-2.5">
            {['官方入口变更提醒', '新增钓鱼手法速报', '真假辨别要点更新'].map((item) => (
              <li key={item} className="flex items-center gap-2.5 text-sm text-primary-100">
                <i className="ri-check-line text-accent-400" />
                {item}
              </li>
            ))}
          </ul>
        </div>

        <div className="rounded-lg bg-background-50 p-6 md:p-8">
          {status === 'success' ? (
            <div className="flex flex-col items-start gap-3">
              <span className="flex h-12 w-12 items-center justify-center rounded-full bg-accent-100 text-accent-700">
                <i className="ri-checkbox-circle-line text-2xl" />
              </span>
              <p className="font-heading text-lg font-bold text-foreground-950">订阅成功，感谢你的信任</p>
              <p className="text-sm leading-relaxed text-foreground-600">
                我们会在官方验证有更新时，发送一封简短提醒到你的邮箱。
              </p>
              <button
                type="button"
                onClick={() => setStatus('idle')}
                className="mt-1 cursor-pointer text-sm font-semibold text-primary-600 hover:text-primary-700"
              >
                再订阅一个邮箱
              </button>
            </div>
          ) : (
            <form data-readdy-form onSubmit={handleSubmit} className="flex flex-col gap-4">
              <div>
                <label htmlFor="sub-email" className="mb-2 block text-sm font-semibold text-foreground-900">
                  邮箱地址
                </label>
                <input
                  id="sub-email"
                  name="email"
                  type="email"
                  required
                  autoComplete="email"
                  placeholder="you@example.com"
                  className="w-full rounded-md border border-background-300 bg-background-50 px-4 py-3 text-sm text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
                />
              </div>

              <div>
                <label htmlFor="sub-topic" className="mb-2 block text-sm font-semibold text-foreground-900">
                  你最关心
                </label>
                <select
                  id="sub-topic"
                  name="topic"
                  defaultValue="safe"
                  className="w-full cursor-pointer rounded-md border border-background-300 bg-background-50 px-4 py-3 text-sm text-foreground-900 outline-none transition-colors focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
                >
                  <option value="safe">黑料地址安全吗</option>
                  <option value="real">真假辨别方法</option>
                  <option value="reliable">靠不靠谱的判断</option>
                  <option value="phishing">是不是钓鱼的提醒</option>
                </select>
              </div>

              <input
                className="form-alt-contact"
                type="text"
                name="contact_alt"
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                readOnly
              />

              <button
                type="submit"
                disabled={status === 'submitting'}
                className="mt-1 inline-flex cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-3.5 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-70"
              >
                <i className={status === 'submitting' ? 'ri-loader-4-line animate-spin' : 'ri-send-plane-fill'} />
                {status === 'submitting' ? '提交中…' : '订阅安全提醒'}
              </button>

              {status === 'error' && formError && (
                <p className="rounded-md border border-primary-200 bg-primary-50 px-4 py-3 text-xs leading-relaxed text-primary-700">
                  {formError}
                </p>
              )}

              <p className="text-xs leading-relaxed text-foreground-500">
                提交即表示你同意我们仅将该邮箱用于安全提醒通知。
              </p>
            </form>
          )}
        </div>
      </div>
    </section>
  );
}